import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgJsonEditorModule } from 'ang-jsoneditor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { loaderFactory } from '@src/app/factories/translation-loader';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import { CoreModule } from '@src/app/core.module';
import { accessInterceptorProvider } from '@src/app/interceptors/access/access.interceptor';
import { scopeInterceptorProvider } from '@src/app/interceptors/scope/scope.interceptor';
import { LayoutModule } from '@angular/cdk/layout';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_RADIO_DEFAULT_OPTIONS, MatRadioModule } from '@angular/material/radio';
import { ButtonComponent } from '@src/app/library/components/button/button.component';
import { JsonModalComponent } from '@src/app/components/modals/json-modal/json-modal.component';
import { EditUserComponent } from './components/user-list/edit-user/edit-user.component';
import { ConfigurationComponent } from '@src/app/components/configurations/configuration.component';
import { RetailerListComponent } from '@src/app/components/retailers/retailer-list.component';
import { BlueprintListComponent } from '@src/app/components/blueprints/blueprint-list.component';
import { LandingComponent } from './components/landing/landing.component';
import { LoginComponent } from './components/login/login.component';
import { PasswordForgottenComponent } from '@src/app/components/password-forgotten/password-forgotten.component';
import { UserListComponent } from '@src/app/components/user-list/user-list.component';
import { MfaComponent } from '@src/app/components/login/mfa/mfa.component';
import { LayoutComponent } from './components/layout/layout.component';
import { ConfirmationModalComponent } from '@src/app/components/modals/confirmation-modal/confirmation-modal.component';
import { ScopeSelectionComponent } from './components/scope-selection/scope-selection.component';
import { CatalogComponent } from '@src/app/components/catalog/catalog.component';
import { RuleTemplateListComponent } from '@src/app/components/rule-templates/rule-template-list.component';
import { RetailerSettingsComponent } from '@src/app/components/retailers/retailer-settings/retailer-settings.component';
import { ModuleCategoriesComponent } from '@src/app/components/catalog/module-categories/module-categories.component';
import { AttributeListComponent } from '@src/app/components/attributes/attribute-list.component';
import { RuleEditorModalComponent } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.component';
import { TranslationsComponent } from '@src/app/components/translations/translations.component';
import { MaterialsComponent } from '@src/app/components/materials/materials.component';
import { TexturesComponent } from '@src/app/components/textures/textures.component';
import { AssetDialogComponent } from '@src/app/components/asset-dialog/asset-dialog.component';
import { RetailerDataComponent } from '@src/app/components/retailers/retailer-data/retailer-data.component';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { MatSortModule } from '@angular/material/sort';
import { NewBlueprintComponent } from '@src/app/components/blueprints/new-blueprint/new-blueprint.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { EanComponent } from '@src/app/components/ean/ean.component';
import { PayloadComponent } from '@src/app/components/payload/payload.component';
import { TypedFormModalComponent } from '@src/app/components/modals/typed-form-modal/typed-form-modal.component';
import { DocumentationComponent } from '@src/app/components/documentation/documentation.component';

registerLocaleData(localeNL, 'nl');

@NgModule({
  declarations: [
    AppComponent,
    LandingComponent,
    LoginComponent,
    MfaComponent,
    PasswordForgottenComponent,
    UserListComponent,
    EditUserComponent,
    RetailerListComponent,
    BlueprintListComponent,
    TranslationsComponent,
    JsonModalComponent,
    ConfirmationModalComponent,
    RuleEditorModalComponent,
    LayoutComponent,
    ButtonComponent,
    ConfigurationComponent,
    RetailerSettingsComponent,
    ScopeSelectionComponent,
    CatalogComponent,
    RuleTemplateListComponent,
    AttributeListComponent,
    ModuleCategoriesComponent,
    MaterialsComponent,
    TexturesComponent,
    RetailerDataComponent,
    AssetDialogComponent,
    NewBlueprintComponent,
    EanComponent,
    PayloadComponent,
    DocumentationComponent,
    TypedFormModalComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgJsonEditorModule,
    MonacoEditorModule.forRoot(),
    CoreModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: loaderFactory,
      },
      fallbackLang: 'en',
    }),
    LayoutModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatDialogModule,
    MatSelectModule,
    MatRadioModule,
    AngularSvgIconModule.forRoot(),
    MatMenuModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatSortModule,
    MatCheckboxModule,
  ],
  providers: [
    accessInterceptorProvider,
    scopeInterceptorProvider,
    { provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})

export class AppModule {
}
